"use client";

// Megaphone next to the Live button: lights up when there's a Spire Codex
// announcement the visitor hasn't seen yet, links to the news tab, and
// clears itself once clicked (or once the tab is visited, via the
// sc-news-seen event MarkAnnouncementsSeen dispatches).
import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { Link } from "@/i18n/navigation";
import { useT } from "@/lib/i18n";
import { ANNOUNCEMENT_SEEN_KEY, LATEST_ANNOUNCEMENT_ID } from "@/lib/announcements";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

/** Shared unread state for the megaphone and the mobile burger dot: latest
 * announcement id (API-first, committed seed as fallback) vs localStorage. */
export function useAnnouncementUnread() {
  const [unread, setUnread] = useState(false);
  const [latestId, setLatestId] = useState(LATEST_ANNOUNCEMENT_ID);
  useEffect(() => {
    let current = LATEST_ANNOUNCEMENT_ID;
    const check = () => {
      try {
        setUnread(!!current && localStorage.getItem(ANNOUNCEMENT_SEEN_KEY) !== current);
      } catch {
        setUnread(false);
      }
    };
    check();
    // Admin-published entries beat the committed seed list: ask the API for
    // the newest id so publishing lights the megaphone without a deploy.
    fetch(`${API}/api/news/codex`).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.latest_id) {
        current = d.latest_id;
        setLatestId(d.latest_id);
        check();
      }
    }).catch(() => {});
    window.addEventListener("sc-news-seen", check);
    return () => window.removeEventListener("sc-news-seen", check);
  }, []);
  const markSeen = () => {
    try {
      localStorage.setItem(ANNOUNCEMENT_SEEN_KEY, latestId);
    } catch {}
    setUnread(false);
    window.dispatchEvent(new Event("sc-news-seen"));
  };
  return {
    unread,
    markSeen
  };
}
function MegaphoneIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden={true}><path d="M3 11v3a1 1 0 0 0 1 1h2l3.5 4.5a1 1 0 0 0 1.8-.6V5.1a1 1 0 0 0-1.8-.6L6 9H4a1 1 0 0 0-1 1z" /><path d="M15 9c1.2 1.6 1.2 4.4 0 6" /><path d="M18 6c2.7 3.2 2.7 8.8 0 12" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export default function AnnouncementBadge(t0) {
  const $ = _c(19);
  const {
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "desktop" : t1;
  const {
    unread,
    markSeen
  } = useAnnouncementUnread();
  const t = useT();
  if (!unread) {
    return null;
  }
  if (variant === "mobile") {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <MegaphoneIcon />;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    let t3;
    if ($[1] !== t) {
      t3 = t("What's new");
      $[1] = t;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] !== t3) {
      t4 = <span>{t3}</span>;
      $[3] = t3;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--accent-gold)] opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--accent-gold)]" /></span>;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== markSeen || $[7] !== t4) {
      t6 = <div className="border-b border-[var(--border-subtle)] px-5 py-3"><Link href="/news?tab=codex" onClick={markSeen} className="flex items-center gap-2 text-lg font-semibold text-[var(--accent-gold)]">{t2}{t4}{t5}</Link></div>;
      $[6] = markSeen;
      $[7] = t4;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  let t2;
  if ($[9] !== t) {
    t2 = t("New on Spire Codex");
    $[9] = t;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== t) {
    t3 = t("New Spire Codex announcement");
    $[11] = t;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <MegaphoneIcon />;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="absolute top-1.5 right-1 flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--accent-gold)] opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--accent-gold)]" /></span>;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== markSeen || $[16] !== t2 || $[17] !== t3) {
    t6 = <Link href="/news?tab=codex" onClick={markSeen} title={t2} aria-label={t3} className="relative inline-flex items-center px-2 py-2 rounded-md text-[var(--accent-gold)] hover:bg-[var(--bg-card)] transition-colors shrink-0">{t4}{t5}</Link>;
    $[15] = markSeen;
    $[16] = t2;
    $[17] = t3;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
}